import React, { useState, useEffect, useRef } from 'react';
import { Upload, Search, Filter, AlertCircle, CheckCircle, X, Info, FolderTree, List, Brain, RefreshCw } from 'lucide-react';
import { Matter } from '../../types';
import { LegalDocument } from '../../services/legalDocumentService';
import { useDocuments } from '../../hooks/useDocuments';
import { CopilotAssistant } from '../copilot/CopilotAssistant';
import { DocumentPreviewPanel } from './DocumentPreviewPanel';
import { SharePointEmbeddedService } from '../../services/sharePointEmbeddedService';
import { SPEAuthProvider } from '../../services/speAuthProvider';
import DocumentTree from './DocumentTree';
import FolderBreadcrumb from './FolderBreadcrumb';
import DocumentList from './DocumentList';
import SimpleContextMenu from './SimpleContextMenu';
import ClassificationTable from './ClassificationTable';
import { SPEDocument } from '../../services/sharePointEmbeddedService';
import copilotIcon from '../../assets/icons/copilotLogo.png';

interface DocumentSectionProps {
  matter: Matter;
  onMatterUpdate?: (matterId: string) => Promise<void>;
}

const DocumentSection: React.FC<DocumentSectionProps> = ({ matter, onMatterUpdate }) => {
  const {
    documents,
    isLoading,
    error,
    uploadProgress,
    uploadDocument,
    deleteDocument,
    loadDocuments,
    downloadDocument,
    clearError,
    // Folder operations
    currentFolderPath,
    navigateToFolder
  } = useDocuments();

  const [isPreviewPanelOpen, setIsPreviewPanelOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<LegalDocument | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [configError, setConfigError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [isChatPanelOpen, setIsChatPanelOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  
  // Phase 3 Enhancement: Folder navigation state
  const [showFolderTree, setShowFolderTree] = useState(false);
  
  // Document Classification state
  const [isClassifying, setIsClassifying] = useState(false);
  const [classificationStatus, setClassificationStatus] = useState<string | null>(null);
  const [classificationResults, setClassificationResults] = useState<any>(null);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  
  // Tab navigation state
  const [activeTab, setActiveTab] = useState<'documents' | 'classification'>('documents');
  
  // Enhanced: Track if we've attempted to load results for this tab session
  const [hasAttemptedResultsLoad, setHasAttemptedResultsLoad] = useState(false);

  // Phase 3.2 Enhancement: Enhanced document interface state
  const [contextMenu, setContextMenu] = useState<{
    isVisible: boolean;
    position: { x: number; y: number };
    document: SPEDocument | null;
  }>({
    isVisible: false,
    position: { x: 0, y: 0 },
    document: null
  });
  
  const [sortConfig, setSortConfig] = useState<{
    column: 'name' | 'size' | 'type' | 'created' | 'modified';
    direction: 'asc' | 'desc';
  }>({
    column: 'name',
    direction: 'asc'
  });

    // Load documents when matter changes
  useEffect(() => {
    const loadDocumentsWithErrorHandling = async () => {
      try {
        clearError();
        const result = await loadDocuments(matter);
        
        // If a container was created during document loading, refresh the matter
        if (result.containerCreated && onMatterUpdate) {
          try {
            console.log('🔄 Container created during document loading, refreshing matter...');
            await onMatterUpdate(matter.id);
          } catch (refreshError) {
            console.warn('Failed to refresh matter after container creation:', refreshError);
          }
        }
      } catch (err) {
        console.error('Document loading error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load documents';
        
        // Check if this is a known setup issue and provide helpful messaging
        if (errorMessage.includes('SETUP_REQUIRED:')) {
          // Special case: setup required - show as informational, not error
          const cleanMessage = errorMessage.replace('SETUP_REQUIRED: ', '');
          setConfigError(`INFO: ${cleanMessage}`);
        } else if (errorMessage.includes('temporarily unavailable') || errorMessage.includes('try again in a moment')) {
          // Temporary API issues - show as warning that will auto-resolve
          setConfigError('INFO: Document service is temporarily busy. Please wait a moment and try again.');
        } else if (errorMessage.includes('Unable to connect to document service')) {
          // Network connectivity issues
          setConfigError('INFO: Unable to connect to document service. Please check your internet connection.');
        } else if (errorMessage.includes('Container Type') || errorMessage.includes('SharePoint Embedded')) {
          setConfigError('INFO: Document system configuration is in progress. Please check back shortly.');
        } else if (errorMessage.includes('permissions') || errorMessage.includes('admin operations')) {
          setConfigError('INFO: Document management is being set up for this matter. This process is automatic and should complete shortly.');
        } else {
          setConfigError('INFO: Documents are not yet available for this matter. The system is working to enable document management.');
        }
      }
    };
    
    loadDocumentsWithErrorHandling();
  }, [matter.id, onMatterUpdate]); // Removed loadDocuments from dependencies to prevent infinite loop

  // Enhanced: Auto-load classification results when switching to classification tab
  useEffect(() => {
    if (activeTab === 'classification' && !hasAttemptedResultsLoad && !classificationResults && matter.speContainerId) {
      console.log('🔄 Auto-loading classification results for classification tab...');
      setHasAttemptedResultsLoad(true);
      handleViewResults();
    }
  }, [activeTab, hasAttemptedResultsLoad, classificationResults, matter.speContainerId]);

  // Toggle chat panel (when container exists)
  const toggleChatPanel = () => {
    if (matter.speContainerId) {
      setIsChatPanelOpen(!isChatPanelOpen);
    }
  };

  // Filter and sort documents based on search, category, and sort configuration
  const filteredAndSortedDocuments = React.useMemo(() => {
    let filtered = documents.filter(doc => {
      const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (doc.tags && doc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
      const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });

    // Convert LegalDocument to SPEDocument format for compatibility
    const speDocuments: SPEDocument[] = filtered.map(doc => ({
      id: doc.id,
      name: doc.name,
      size: doc.size,
      createdDateTime: doc.createdDateTime || new Date().toISOString(),
      lastModifiedDateTime: doc.lastModifiedDateTime || new Date().toISOString(),
      webUrl: doc.webUrl || '',
      mimeType: doc.mimeType,
      createdBy: doc.createdBy,
      lastModifiedBy: doc.lastModifiedBy
    }));

    // Apply sorting
    return speDocuments.sort((a, b) => {
      const { column, direction } = sortConfig;
      let comparison = 0;

      switch (column) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
        case 'type':
          const aExt = a.name.split('.').pop()?.toLowerCase() || '';
          const bExt = b.name.split('.').pop()?.toLowerCase() || '';
          comparison = aExt.localeCompare(bExt);
          break;
        case 'created':
          comparison = new Date(a.createdDateTime).getTime() - new Date(b.createdDateTime).getTime();
          break;
        case 'modified':
          comparison = new Date(a.lastModifiedDateTime).getTime() - new Date(b.lastModifiedDateTime).getTime();
          break;
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  }, [documents, searchTerm, selectedCategory, sortConfig]);

  // Handle file upload via drag & drop or file input
  const handleFileUpload = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    console.log(`📂 Upload start: currentFolderPath = "${currentFolderPath}"`);
    setUploadStatus('uploading');
    
    try {
      for (const file of fileArray) {
        const result = await uploadDocument(file, matter, 'other', 'internal', [], currentFolderPath);
        if (!result.success) {
          throw new Error(result.error || 'Upload failed');
        }
      }
      setUploadStatus('success');
      
      console.log(`✅ Upload complete: currentFolderPath = "${currentFolderPath}"`);
      
      // Only refresh matter data if the matter doesn't have a container ID yet
      if (!matter.speContainerId && onMatterUpdate) {
        console.log(`🔄 Matter has no container ID, calling onMatterUpdate...`);
        try {
          await onMatterUpdate(matter.id);
          console.log(`✅ onMatterUpdate completed`);
        } catch (refreshError) {
          console.warn('Failed to refresh matter after upload:', refreshError);
        }
      } else {
        console.log(`✅ Matter already has container ID, skipping onMatterUpdate`);
      }
      
      setTimeout(() => setUploadStatus('idle'), 3000);
    } catch (err) {
      setUploadStatus('error');
      console.error('Upload error:', err);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0; // Reset to 0 to prevent negative values
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  // File input handler
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
  };

  // Handle download
  const handleDownload = async (doc: LegalDocument) => {
    await downloadDocument(doc, matter);
  };

  // Handle delete with confirmation
  const handleDelete = async (doc: LegalDocument) => {
    if (window.confirm(`Are you sure you want to delete "${doc.name}"? This action cannot be undone.`)) {
      await deleteDocument(doc, matter);
    }
  };

  // Phase 3.2 Enhancement: Document action handlers
  const handleDocumentAction = async (action: string, document: SPEDocument, event?: React.MouseEvent) => {
    const doc = document as unknown as LegalDocument; // Type conversion for compatibility
    
    switch (action) {
      case 'download':
        await handleDownload(doc);
        break;
      case 'preview':
        await handleDocumentView(doc);
        break;
      case 'delete':
        await handleDelete(doc);
        break;
      case 'showContextMenu':
        // Show context menu at button position
        if (event && event.currentTarget) {
          const buttonRect = (event.currentTarget as HTMLElement).getBoundingClientRect();
          setContextMenu({
            document: doc,
            position: { 
              x: buttonRect.left + buttonRect.width / 2, 
              y: buttonRect.bottom + 5 
            },
            isVisible: true
          });
        }
        break;
      case 'copyLink':
        // Generate and copy shareable link
        if (matter.speContainerId) {
          try {
            const authProvider = new SPEAuthProvider();
            const speService = new SharePointEmbeddedService(authProvider);
            const previewUrl = await speService.getDocumentPreviewUrl(matter.speContainerId, document.id);
            await navigator.clipboard.writeText(previewUrl);
            // Show toast notification here
          } catch (error) {
            console.error('Failed to copy link:', error);
          }
        }
        break;
      case 'showProperties':
        // Show document properties modal
        console.log('Show properties for:', document.name);
        break;
      case 'refreshPreview':
        // Clear preview cache and regenerate
        console.log('Refresh preview for:', document.name);
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  // Handle sorting
  const handleSort = (column: string, direction: 'asc' | 'desc') => {
    setSortConfig({ column: column as any, direction });
  };

  // Handle context menu
  const closeContextMenu = () => {
    setContextMenu(prev => ({ ...prev, isVisible: false }));
  };

  // Handle document view
  const handleDocumentView = async (doc: LegalDocument) => {
    // Check if this is an Office document that should open directly in new tab
    const isOfficeDoc = ['.docx', '.xlsx', '.pptx', '.doc', '.xls', '.ppt'].some(ext => 
      doc.name.toLowerCase().endsWith(ext)
    );
    
    if (isOfficeDoc) {
      // For Office documents, show loading state and get the preview URL
      if (!matter.speContainerId) {
        console.error('No SharePoint container ID available for matter');
        // Fall back to side panel if no container ID
        setPreviewDocument(doc);
        setIsPreviewPanelOpen(true);
        return;
      }
      
      try {
        const authProvider = new SPEAuthProvider();
        const speService = new SharePointEmbeddedService(authProvider);
        
        let previewUrl: string;
        try {
          previewUrl = await speService.getDocumentEmbedUrl(matter.speContainerId, doc.id);
        } catch {
          // Fallback to preview URL
          previewUrl = await speService.getDocumentPreviewUrl(matter.speContainerId, doc.id);
        }
        
        // Open in new tab immediately
        window.open(previewUrl, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Failed to open Office document:', error);
        // If direct opening fails, fall back to side panel
        setPreviewDocument(doc);
        setIsPreviewPanelOpen(true);
      }
    } else {
      // For PDFs and other documents, use the side panel
      setPreviewDocument(doc);
      setIsPreviewPanelOpen(true);
    }
  };

  // Close document preview panel
  const closePreviewPanel = () => {
    setIsPreviewPanelOpen(false);
    setPreviewDocument(null);
  };

  // Handle fetching classification results
  const handleViewResults = async (showSuccessMessage: boolean = false) => {
    if (!matter.speContainerId) {
      setClassificationStatus('Error: No document container available');
      return;
    }

    setIsLoadingResults(true);
    
    try {
      // Get API URL using same logic as apiClient.ts and AuthContext.tsx
      let baseURL = import.meta.env.VITE_API_BASE_URL;
      
      if (!baseURL && typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          baseURL = 'http://localhost:7071/api';
        } else {
          baseURL = '/api';
        }
      } else if (!baseURL) {
        baseURL = 'http://localhost:7071/api';
      }

      const response = await fetch(`${baseURL}/matters/${matter.id}/classification-results`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });

      const result = await response.json();

      if (response.ok) {
        setClassificationResults(result);
        setActiveTab('classification');
        
        if (showSuccessMessage) {
          setClassificationStatus(`✅ Classification results refreshed (${result.totalDocuments} documents)`);
          setTimeout(() => {
            setClassificationStatus(null);
          }, 3000);
        }
      } else {
        if (result.error === "No classification results found for this matter") {
          setClassificationStatus(`ℹ️ No classification results yet. Run document classification first.`);
        } else {
          setClassificationStatus(`❌ ${result.error || 'Failed to load results'}`);
        }
        
        setTimeout(() => {
          setClassificationStatus(null);
        }, 5000);
      }
    } catch (error) {
      console.error('Error fetching classification results:', error);
      setClassificationStatus(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => {
        setClassificationStatus(null);
      }, 5000);
    } finally {
      setIsLoadingResults(false);
    }
  };

  // Handle document classification
  const handleClassifyDocuments = async () => {
    if (!matter.speContainerId) {
      setClassificationStatus('Error: No document container available');
      return;
    }

    setIsClassifying(true);
    setClassificationStatus('Starting classification...');

    try {
      // Get API URL using same logic as apiClient.ts and AuthContext.tsx
      let baseURL = import.meta.env.VITE_API_BASE_URL;
      
      if (!baseURL && typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          baseURL = 'http://localhost:7071/api';
        } else {
          baseURL = '/api';
        }
      } else if (!baseURL) {
        baseURL = 'http://localhost:7071/api';
      }

      const classificationData = {
        classificationType: 'receipt',
        folderPath: currentFolderPath || undefined // Use current folder if user is in a specific folder
      };

      const response = await fetch(`${baseURL}/matters/${matter.id}/classify-documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: JSON.stringify(classificationData)
      });

      const result = await response.json();

      if (response.ok) {
        setClassificationStatus(`✅ Classification complete! Processed ${result.classification.processedDocuments} documents`);
        
        // Refresh document list to show new classification files
        await loadDocuments(matter);
        
        // Enhanced: Auto-load results after successful classification
        setTimeout(async () => {
          console.log('🔄 Auto-loading results after successful classification...');
          await handleViewResults(false); // Load results without success message
          setClassificationStatus(null);
        }, 2000);
      } else {
        setClassificationStatus(`❌ Classification failed: ${result.error}`);
        setTimeout(() => {
          setClassificationStatus(null);
        }, 5000);
      }
    } catch (error) {
      console.error('Classification error:', error);
      setClassificationStatus(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => {
        setClassificationStatus(null);
      }, 5000);
    } finally {
      setIsClassifying(false);
    }
  };



  return (
    <div className="p-0 relative">
      {/* Configuration Error Display */}
      {configError && (
        <div className="m-6 mb-4 p-4 bg-blue-900/30 border border-blue-600/50 rounded-lg">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              {configError.startsWith('INFO: ') ? (
                <>
                  <h4 className="text-blue-200 font-semibold mb-2">Document Setup in Progress</h4>
                  <p className="text-blue-200 text-sm mb-3">{configError.replace('INFO: ', '')}</p>
                  <div className="text-blue-300 text-sm">
                    <p className="mb-2">What's happening:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-blue-200">
                      <li>Document storage is being configured for this matter</li>
                      <li>This process typically completes within a few minutes</li>
                      <li>You can continue working with other parts of the matter</li>
                    </ul>
                    <p className="mt-3 text-xs text-blue-300">
                      You can refresh the page or return later to access documents.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <h4 className="text-blue-200 font-semibold mb-2">SharePoint Embedded Configuration Required</h4>
                  <p className="text-blue-200 text-sm mb-3">{configError}</p>
                  <div className="text-blue-200 text-sm">
                    <p className="mb-2"><strong>To fix this issue:</strong></p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Open SharePoint Admin Center</li>
                      <li>Go to Settings → Content services → SharePoint Embedded</li>
                      <li>Register a new Container Type or update the existing one</li>
                      <li>Update the Container Type ID in the application configuration</li>
                    </ol>
                    <p className="mt-3 text-xs">
                      Current Container Type ID: <code className="bg-blue-800 px-1 py-0.5 rounded">61f4d9ea-d687-45eb-a8d1-3ff960937f80</code>
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && !configError && (
        <div className="mb-4 p-4 bg-red-900 border border-red-700 rounded-lg flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-200">{error}</span>
          </div>
          <button
            onClick={clearError}
            className="text-red-400 hover:text-red-300"
          >
            ×
          </button>
        </div>
      )}

      {/* Upload Status */}
      {uploadStatus === 'success' && (
        <div className="mb-4 p-4 bg-green-900 border border-green-700 rounded-lg flex items-center space-x-2">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-200">Documents uploaded successfully!</span>
        </div>
      )}

      {/* Classification Status */}
      {classificationStatus && (
        <div className="mb-4 p-4 bg-purple-900 border border-purple-700 rounded-lg flex items-center space-x-2">
          <Brain className="w-5 h-5 text-purple-400" />
          <span className="text-purple-200">{classificationStatus}</span>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="px-6 mb-6">
        <div className="flex items-center space-x-1 border-b border-gray-700">
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'documents'
                ? 'bg-gray-700 text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            Documents ({documents.length})
          </button>
          <button
            onClick={() => setActiveTab('classification')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'classification'
                ? 'bg-gray-700 text-white border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            Classification Results
            {classificationResults && (
              <span className="ml-1 text-xs bg-purple-600 text-white px-1.5 py-0.5 rounded-full">
                {classificationResults.totalDocuments}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Dynamic Header */}
      <div className="flex items-center justify-between mb-6 px-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-200 mb-2">
            {activeTab === 'documents' ? 'Documents' : 'Classification Results'}
          </h3>
          <p className="text-gray-400">
            {activeTab === 'documents' ? (
              <>
                Manage documents for {matter.title} ({documents.length} files)
                {currentFolderPath && <span className="text-blue-400"> - in folder: {currentFolderPath}</span>}
              </>
            ) : (
              <>
                Receipt analysis and line items for {matter.title}
                {classificationResults && (
                  <span className="text-purple-400"> - {classificationResults.totalDocuments} documents classified</span>
                )}
              </>
            )}
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center space-x-3">
          {activeTab === 'documents' ? (
            <>
              {/* Classification Button */}
              {!configError && matter.speContainerId && (
                <button
                  onClick={handleClassifyDocuments}
                  disabled={isClassifying}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                    isClassifying 
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                  title={currentFolderPath ? `Classify documents in ${currentFolderPath}` : 'Classify all documents'}
                >
                  <Brain className={`w-4 h-4 ${isClassifying ? 'animate-pulse' : ''}`} />
                  <span>{isClassifying ? 'Classifying...' : 'Classify Documents'}</span>
                </button>
              )}
              
              {/* View Results Button */}
              {!configError && matter.speContainerId && (
                <button
                  onClick={() => handleViewResults(true)}
                  disabled={isLoadingResults}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                    isLoadingResults 
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                  title="View classification results"
                >
                  <Search className={`w-4 h-4 ${isLoadingResults ? 'animate-pulse' : ''}`} />
                  <span>{isLoadingResults ? 'Loading...' : 'View Results'}</span>
                </button>
              )}
              
              {/* Upload Button */}
              {!configError && (
                <div className="relative">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileInputChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                  />
                  <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                    <Upload className="w-4 h-4" />
                    <span>Upload Files</span>
                  </button>
                </div>
              )}
              
              {configError && (
                <div className="px-4 py-2 border border-gray-600 rounded-md bg-gray-800 opacity-50">
                  <div className="flex items-center space-x-2">
                    <Upload className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-500">Upload disabled</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Classification Tab Actions */
            <>
              {/* Refresh Results Button */}
              {!configError && matter.speContainerId && (
                <button
                  onClick={() => handleViewResults(true)}
                  disabled={isLoadingResults}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                    isLoadingResults 
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                  title="Refresh classification results"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoadingResults ? 'animate-spin' : ''}`} />
                  <span>{isLoadingResults ? 'Refreshing...' : 'Refresh Results'}</span>
                </button>
              )}
              
              {/* Re-run Classification Button */}
              {!configError && matter.speContainerId && (
                <button
                  onClick={handleClassifyDocuments}
                  disabled={isClassifying}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                    isClassifying 
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                  title="Re-run document classification"
                >
                  <Brain className={`w-4 h-4 ${isClassifying ? 'animate-pulse' : ''}`} />
                  <span>{isClassifying ? 'Classifying...' : 'Re-run Classification'}</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Upload Progress */}
      {uploadProgress > 0 && uploadProgress < 100 && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm text-gray-400 mb-1">
            <span>Uploading...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'documents' ? (
        /* Documents Tab - Original Content */
        <div className={`relative ${isPreviewPanelOpen ? 'documents-with-preview-layout' : ''}`}>
        <div className="documents-main-content transition-all duration-300 px-6">
          <div className="flex space-x-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-400 focus:border-blue-500 focus:outline-none"
              />
            </div>
            
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="pl-10 pr-8 py-2 bg-gray-800 border border-gray-600 rounded-lg text-gray-200 focus:border-blue-500 focus:outline-none appearance-none"
              >
                <option value="all">All Categories</option>
                <option value="contract">Contracts</option>
                <option value="correspondence">Correspondence</option>
                <option value="research">Research</option>
                <option value="evidence">Evidence</option>
                <option value="pleading">Pleadings</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* View Mode Controls */}
            <div className="flex items-center border border-gray-600 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowFolderTree(true)}
                className={`p-2 transition-colors ${
                  showFolderTree 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                }`}
                title="Show folder tree"
              >
                <FolderTree className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowFolderTree(false)}
                className={`p-2 transition-colors ${
                  !showFolderTree 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                }`}
                title="List view only"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Chat Toggle Button - Always show when container exists */}
            {matter.speContainerId && (
              <button
                onClick={toggleChatPanel}
                className={`flex items-center justify-center p-2 rounded-lg transition-all hover:bg-gray-700/50 ${
                  isChatPanelOpen ? 'bg-gray-700/50' : 'bg-transparent'
                }`}
                title={isChatPanelOpen ? 'Hide AI Assistant' : 'Show AI Assistant'}
              >
                <img src={copilotIcon} alt="Copilot" className="w-9 h-9 object-contain" />
                {isChatPanelOpen && <X className="w-4 h-4 ml-1 text-gray-400" />}
              </button>
            )}
          </div>

          {/* Folder Breadcrumb */}
          {!configError && (
            <div className="mb-4 pl-1">
              <FolderBreadcrumb 
                currentPath={currentFolderPath || ""} 
                onNavigate={(path) => navigateToFolder(matter, path)} 
                className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-3"
              />
            </div>
          )}

          {/* Main Content Area with Optional Folder Tree */}
          <div className={`flex gap-6 ${showFolderTree ? 'with-folder-tree' : ''}`}>
            {/* Folder Tree Side Panel */}
            {showFolderTree && !configError && (
              <div className="w-64 flex-shrink-0">
                <DocumentTree
                  matter={matter}
                  currentPath={currentFolderPath || ""}
                  onFolderSelect={(path) => navigateToFolder(matter, path)}
                />
              </div>
            )}

            {/* Document List Area with Drag & Drop */}
            <div 
              className={`flex-1 min-w-0 relative ${isDragOver ? 'bg-blue-900/10' : ''}`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* Drag overlay for document list area */}
              {isDragOver && !configError && (
                <div className="absolute inset-0 bg-blue-900/20 border-2 border-dashed border-blue-400 rounded-lg z-10 flex items-center justify-center">
                  <div className="text-center">
                    <Upload className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                    <p className="text-lg text-blue-300 font-medium">Drop files to upload</p>
                    <p className="text-sm text-blue-200 mt-2">Files will be added to this matter</p>
                  </div>
                </div>
              )}
              
              {/* Enhanced Document List with Microsoft Graph API integration */}
              <DocumentList
                documents={filteredAndSortedDocuments}
                isLoading={isLoading}
                onDocumentAction={handleDocumentAction}
                onSort={handleSort}
                sortConfig={sortConfig}
                className="bg-gray-800 border border-gray-700"
              />
            </div>
          </div>
        </div>
        
        {/* Context Menu */}
        {contextMenu.document && (
          <SimpleContextMenu
            document={contextMenu.document}
            position={contextMenu.position}
            isVisible={contextMenu.isVisible}
            onClose={closeContextMenu}
            onAction={handleDocumentAction}
          />
        )}
        
        {/* Chat Side Panel - Now overlay like preview */}
        {matter.speContainerId && (
          <div className={`documents-chat-panel ${isChatPanelOpen ? '' : 'collapsed'}`}>
            <div className="h-full flex flex-col">
              {/* Chat Header with Close Button */}
              <div className="flex items-center justify-between p-4 border-b border-gray-600 bg-gray-800">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <img src={copilotIcon} alt="Copilot" className="w-5 h-5 mr-2" />
                  AI Assistant
                </h3>
                <button
                  onClick={() => setIsChatPanelOpen(false)}
                  className="p-1 hover:bg-gray-700 rounded transition-colors"
                  title="Close AI Assistant"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              {/* Chat Content */}
              <div className="flex-1 h-full overflow-hidden">
                <CopilotAssistant
                  containerId={matter.speContainerId}
                  matterTitle={matter.title}
                />
              </div>
            </div>
          </div>
        )}

        {/* Document Preview Panel */}
        {matter.speContainerId && (
          <DocumentPreviewPanel
            document={previewDocument}
            containerId={matter.speContainerId}
            isOpen={isPreviewPanelOpen}
            onClose={closePreviewPanel}
          />
        )}

        </div>
      ) : (
        /* Classification Tab - Table Content */
        <div className="px-6">
          <ClassificationTable results={classificationResults} />
        </div>
      )}
    </div>
  );
};

export default DocumentSection;
